import React, { useEffect } from "react";
import SEO from "../components/SEO";
import AOS from "aos";
import "aos/dist/aos.css";

const TermsConditions = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
      offset: 100
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#000000] pt-20 sm:pt-24">
      <SEO 
        title="Terms & Conditions"
        description="Read our Terms & Conditions to understand the terms of service for using Book Creation Studio's publishing services."
        keywords="terms and conditions, terms of service, user agreement, Book Creation Studio"
        url="/terms-conditions"
      />

      {/* Hero Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-[#1a1a1a] to-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F4D03F] mb-4 uppercase">
              Terms & Conditions
            </h1>
            <p className="text-lg text-[#dfddd6]">
              Last Updated: January 1, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-invert max-w-none">

              {/* Copyrights */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Copyrights</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] leading-relaxed">
                    At Book Creation Studio, we are committed to empowering authors with full ownership of their works. Upon delivery of the final product, you retain all rights to your content and have the freedom to use it as you wish.
                  </p>
                </div>
              </div>

              {/* Payment */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Payment</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] leading-relaxed">
                    All services provided by Book Creation Studio require full payment upfront. Clients are also responsible for any applicable taxes related to the payment.
                  </p>
                </div>
              </div>

              {/* Revisions */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Revisions</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-3">We offer a 14-day period for revisions, which can be extended under exceptional circumstances. The following categories apply for revision turnaround times:</p>
                  <ul className="list-disc list-inside text-[#dfddd6] space-y-2 ml-4">
                    <li><strong className="text-[#F4D03F]">24-hour urgency:</strong> Revisions will be completed within 24 hours.</li>
                    <li><strong className="text-[#F4D03F]">24-48 hour urgency:</strong> Revisions will be completed within 48 hours.</li>
                    <li><strong className="text-[#F4D03F]">More than 48-hour urgency:</strong> Revisions will be completed within 72 hours.</li>
                  </ul>
                </div>
              </div>

              {/* Project Terms */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Project Terms</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] leading-relaxed">
                    Before beginning any project, Book Creation Studio will conduct a one-on-one consultation to ensure we fully understand your requirements. Throughout the project, we will maintain clear communication regarding any changes or updates.
                  </p>
                </div>
              </div>

              {/* Terms of Use */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Terms of Use</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    Book Creation Studio's services are designed to help authors achieve their publishing goals. While we do not provide certifications or warranties, we ensure a high standard of service. We are not responsible for any delays, operational failures, data loss, or unauthorized access.
                  </p>
                  <p className="text-[#dfddd6] leading-relaxed">
                    By using our services, you agree to indemnify Book Creation Studio against any third-party claims related to intellectual property violations, privacy breaches, or any use of our website and services.
                  </p>
                </div>
              </div>

              {/* Refund Policy */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Refund Policy</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    At Book Creation Studio, we take pride in offering premium book publishing services designed to deliver outstanding results through a collaborative, multi-expert approach. To maintain the integrity of our work and ensure fair compensation for all team members involved, we uphold the following refund policy:
                  </p>
                  
                  <h3 className="text-xl font-bold text-[#F4D03F] mb-3">No Refunds After Project Commencement</h3>
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    Due to the customized and labor-intensive nature of our services-including manuscript editing, cover design, formatting, publishing setup, marketing, ghostwriting, and consultations-we do not offer refunds once a project has commenced.
                  </p>
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    Once work begins, multiple professionals are assigned to different aspects of your publishing package. Each contributor commits time, expertise, and resources from the outset. As such, we are unable to accommodate cancellations or refund requests after project initiation.
                  </p>
                  
                  <h3 className="text-xl font-bold text-[#F4D03F] mb-3">Unlimited Revisions Guarantee</h3>
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    To ensure your satisfaction, we offer unlimited revisions within the scope of the original project brief. This applies to editing, design, formatting, and other deliverables as specified in your selected package. Our goal is to meet or exceed your expectations before final delivery.
                  </p>
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    <strong className="text-[#F4D03F]">Note:</strong> Revisions beyond the original scope or that involve significant directional changes may incur additional charges, which will be communicated in advance.
                  </p>
                  
                  <h3 className="text-xl font-bold text-[#F4D03F] mb-3">Project Cancellation Before Start</h3>
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    If you choose to cancel your project before any work has begun, you may be eligible for a partial refund, minus any administrative or consultation fees already incurred. Please contact us within 24 hours of purchase for eligibility assessment.
                  </p>
                  
                  <h3 className="text-xl font-bold text-[#F4D03F] mb-3">Disputes and Chargebacks</h3>
                  <p className="text-[#dfddd6] leading-relaxed">
                    If you believe a billing error has occurred, please contact us directly at info@bookcreationstudio.com so we can resolve the issue quickly and professionally. Initiating a chargeback without prior communication may result in suspension of current and future services.
                  </p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Disclaimer</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    All logos, trademarks, and content on our website are the property of their respective owners. Book Creation Studio is not affiliated with or endorsed by these entities. Any logos or content displayed are for identification purposes only.
                  </p>
                  <p className="text-[#dfddd6] leading-relaxed">
                    The information on our website is intended for general purposes and should not be construed as professional advice. Book Creation Studio does not guarantee the accuracy of the information and is not responsible for any errors or omissions. Any reliance on such information is at your own risk.
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Contact Us</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-3">
                    If you have any questions about these Terms & Conditions, please contact us:
                  </p>
                  <ul className="text-[#dfddd6] space-y-2">
                    <li><strong className="text-[#F4D03F]">Email:</strong> info@bookcreationstudio.com</li>
                    <li><strong className="text-[#F4D03F]">Phone:</strong> +1(833) 201 7583</li>
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;